import type { ReactNode } from 'react'

import { t } from '../i18n'
import { useBackButton, useGoBack } from '../lib/navigation'
import type { BackTarget } from '../lib/navigation'
import { haptic, insideTelegram } from '../lib/telegram'
import { Icon } from './Icon'

type ScreenProps = {
  title: string
  subtitle?: ReactNode
  eyebrow?: string
  aside?: ReactNode
  nested?: boolean
  back?: BackTarget
  onBack?: () => void
  children: ReactNode
}

export function Screen({
  title,
  subtitle,
  eyebrow,
  aside,
  nested,
  back,
  onBack,
  children,
}: ScreenProps) {
  const goBack = useGoBack(back)
  useBackButton(onBack ?? goBack, Boolean(nested))

  return (
    <main
      className={`backdrop-blobs min-h-dvh px-4 pt-[calc(var(--spacing-safe-top)+16px)] ${
        nested ? 'pb-[calc(var(--spacing-safe-bottom)+24px)]' : 'pb-nav'
      }`}
    >
      {nested && !insideTelegram ? (
        <button
          type="button"
          onClick={() => {
            haptic.select()
            ;(onBack ?? goBack)()
          }}
          aria-label={t.common.back}
          className="pressable tap-area mb-[14px] flex h-[34px] w-[34px] items-center justify-center rounded-capsule border border-glass bg-glass-2 text-text-strong transition-transform duration-press active:-translate-x-[2px]"
        >
          <Icon name="chevron-left" size={17} />
        </button>
      ) : null}

      <header className="relative flex items-end">
        <div className="min-w-0 flex-1 pr-[92px]">
          {eyebrow ? (
            <div className="text-label uppercase text-text-secondary">{eyebrow}</div>
          ) : null}
          <h1 className="mt-1 text-display">{title}</h1>
        </div>
        {aside ? <div className="absolute bottom-0 right-0">{aside}</div> : null}
      </header>

      {subtitle ? <p className="mt-[6px] text-hint text-text-muted">{subtitle}</p> : null}

      <div className="mt-4">{children}</div>
    </main>
  )
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="mb-[9px] mt-5 text-label uppercase text-text-secondary">{children}</div>
}
